import { useState, useEffect } from "react";
import axios from "axios";
import { LogOut, RefreshCw, Lock } from "lucide-react";
import { API } from "../data/site";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const [token, setToken] = useState(sessionStorage.getItem("ssi_admin") || "");
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Admin — Quote Leads | Sana Steel Industries";
  }, []);

  const load = async (pwd) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/leads`, { password: pwd });
      setLeads(data.leads);
      sessionStorage.setItem("ssi_admin", pwd);
      setToken(pwd);
    } catch (e) {
      setError(e.response?.status === 401 ? "Incorrect password." : "Could not load leads. Try again.");
      sessionStorage.removeItem("ssi_admin");
      setToken("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && leads === null) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    sessionStorage.removeItem("ssi_admin");
    setToken("");
    setLeads(null);
    setPassword("");
  };

  if (!token) {
    return (
      <main className="pt-20 min-h-screen bg-surface flex items-center justify-center px-5" data-testid="admin-login-page">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load(password);
          }}
          className="w-full max-w-sm bg-white border border-gray-200 p-10"
        >
          <span className="w-12 h-12 bg-navy flex items-center justify-center">
            <Lock size={20} className="text-white" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-black uppercase tracking-tight text-navy">
            Quote Leads
          </h1>
          <p className="mt-2 text-sm text-steel">Sana Steel Industries — admin access only.</p>
          <label htmlFor="admin-pwd" className="field-label mt-8">Password</label>
          <input
            id="admin-pwd"
            type="password"
            data-testid="admin-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="field"
            placeholder="Admin password"
            required
          />
          {error && (
            <p className="mt-3 text-sm text-red-600" data-testid="admin-login-error">{error}</p>
          )}
          <button type="submit" disabled={loading} data-testid="admin-login-button"
                  className="btn-primary w-full mt-8 disabled:opacity-60">
            {loading ? "Checking…" : "View Leads"}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="pt-20 min-h-screen bg-surface" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-12">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight text-navy">
              Quote Leads
            </h1>
            <p className="mt-1 text-sm text-steel" data-testid="admin-leads-count">
              {leads ? `${leads.length} enquiry${leads.length === 1 ? "" : "ies"} received` : "Loading…"}
            </p>
          </div>
          <div className="flex gap-3">
            <button onClick={() => load(token)} data-testid="admin-refresh-button"
                    className="inline-flex items-center gap-2 border border-gray-300 text-navy font-heading text-xs font-bold uppercase tracking-wide px-5 py-3 hover:bg-white transition-colors duration-200">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout-button"
                    className="inline-flex items-center gap-2 bg-navy text-white font-heading text-xs font-bold uppercase tracking-wide px-5 py-3 hover:bg-steel transition-colors duration-200">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        <div className="mt-8 overflow-x-auto border border-gray-200 bg-white" data-testid="admin-leads-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="bg-navy text-white">
                {["Date", "Name", "Company", "Phone", "Email", "Product", "Qty", "Message"].map((h) => (
                  <th key={h} className="text-left font-heading text-xs font-bold uppercase tracking-[0.15em] px-4 py-4">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leads && leads.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-steel">
                    No enquiries yet. New quote requests will appear here.
                  </td>
                </tr>
              )}
              {leads &&
                leads.map((l, i) => (
                  <tr key={l.id || i} className={i % 2 === 0 ? "bg-white" : "bg-surface"} data-testid={`lead-row-${i}`}>
                    <td className="px-4 py-3 border-b border-gray-200 text-steel whitespace-nowrap">{fmtDate(l.created_at)}</td>
                    <td className="px-4 py-3 border-b border-gray-200 font-semibold text-ink whitespace-nowrap">{l.name}</td>
                    <td className="px-4 py-3 border-b border-gray-200 text-ink">{l.company || "—"}</td>
                    <td className="px-4 py-3 border-b border-gray-200">
                      <a href={`tel:${l.phone}`} className="text-navy hover:text-steel whitespace-nowrap">{l.phone}</a>
                    </td>
                    <td className="px-4 py-3 border-b border-gray-200">
                      <a href={`mailto:${l.email}`} className="text-navy hover:text-steel">{l.email}</a>
                    </td>
                    <td className="px-4 py-3 border-b border-gray-200 text-ink whitespace-nowrap">{l.product}</td>
                    <td className="px-4 py-3 border-b border-gray-200 text-ink">{l.quantity || "—"}</td>
                    <td className="px-4 py-3 border-b border-gray-200 text-steel max-w-xs">{l.message || "—"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
}
